import type { Match } from "navigo";
import type { ChildDom } from "vanjs-core";
import { reactive, replace } from "vanjs-ext";
import { modal } from "..";
import { PriceWrapper, prices } from "../billing/prices";
import { subscriptionIsActive } from "../device/device";
import { errorSection } from "../error-section";
import { loadingScreen } from "../loading-screen";
import { getWeekdays, t } from "../locale";
import { removeNotificationListener } from "../mobile";
import type { Route, RouteContext } from "../router";
import { state } from "../state";
import { deviceSettingsBox } from "../van/device-settings-box";
import { materialSymbol } from "../van/material-symbol";
import { onOffSelect } from "../van/on-off-select";
import {
	button,
	div,
	input,
	label,
	option,
	select,
	span,
	table,
	tbody,
	td,
	tr,
} from "../van/van-tags";
import { sp2Placeholder, type Smartplug2, type TimerRule } from "./sp2";

export class Sp2SettingsPage implements Route {
	#ctx: RouteContext;

	#reactive: Shared = reactive({
		device: sp2Placeholder,
		deviceId: "",
		isOwner: false,
		prices: [] as [string, PriceWrapper][],
		subscriptionIsActive: false,
	});

	#save = async (): Promise<void> => {
		const req = this.#ctx.api.put(
			`/smartplug2s/${this.#reactive.device.Imei}`,
		);
		req.setJsonBody(this.#reactive.device);
		req.setSignedInUserAuth();

		errorSection.clear();
		const res = await loadingScreen.waitFor(req.do());
		if (res.ok) {
			this.#reactive.device = await res.json();
		} else {
			errorSection.setText(await res.text());
		}
	};

	#unregister = async (): Promise<void> => {
		this.#reactive.device.Metadata.OwnerEmail = "";
		this.#save();
		removeNotificationListener("smartplug2", this.#reactive.deviceId);
		this.#ctx.router.jump("/devices");
	};

	ChildDom: ChildDom;

	refresh = async (match: Match): Promise<void> => {
		if (!match?.data?.imei) throw "missing path param: imei";
		errorSection.clear();

		const user = await state.getActiveUser();

		const { Smartplug2Month, Smartplug2Year } = await prices;
		this.#reactive.prices = [
			["", new PriceWrapper(user.Country)],
			[
				Smartplug2Month.Id,
				new PriceWrapper(user.Country, Smartplug2Month),
			],
			[Smartplug2Year.Id, new PriceWrapper(user.Country, Smartplug2Year)],
		];

		const req = this.#ctx.api.get(`/smartplug2s/${match.data.imei}`);
		req.setSignedInUserAuth();

		const res = await loadingScreen.waitFor(req.do());
		if (res.ok) {
			const sp2 = (await res.json()) as Smartplug2;
			this.#reactive.device = replace(this.#reactive.device, sp2);
			this.#reactive.deviceId = sp2.Imei;
			this.#reactive.isOwner = sp2.Metadata.OwnerEmail === user.Email;
			this.#reactive.subscriptionIsActive = subscriptionIsActive(sp2);
		} else {
			errorSection.setText(await res.text());
		}
	};

	constructor(ctx: RouteContext) {
		this.#ctx = ctx;
		this.ChildDom = deviceSettingsBox(
			{
				ctx: this.#ctx,
				device: this.#reactive.device,
				deviceIdType: "IMEI",
				deviceType: "SmartPlug2",
				fields: [
					consumptionField(this.#reactive, this.#save),
					deviceBeepField(this.#reactive),
					powerFailureAlarmField(this.#reactive),
					powerLoadAlarmField(this.#reactive),
					timerField(this.#reactive),
					fotaField(this.#reactive, this.#save),
				],
				isOwner: this.#reactive.isOwner,
				prices: this.#reactive.prices,
				save: () => this.#save(),
				unregister: () => this.#unregister(),
			},
			this.#reactive,
		);
	}
}

interface Shared {
	device: Smartplug2;
	deviceId: string;
	isOwner: boolean;
	prices: [string, PriceWrapper][];
	subscriptionIsActive: boolean;
}

const consumptionField = (shared: Shared, save: () => void): ChildDom => {
	const onClickReset = (): void => {
		shared.device.Consumption = 0;
		save();
	};

	return div(
		{ class: "field" },
		label(
			{ class: "label" },
			() => t.smartplug.settingsPage.consumptionLabel,
		),
		div(
			{ class: "field is-grouped" },
			div(
				{ class: "control" },
				button(
					{
						class: "button is-primary",
						onclick: onClickReset,
					},
					() => t.reset,
				),
			),
			div(
				{ class: "control" },
				input({
					class: "input is-static",
					value: () => `${shared.device.Consumption} Wh`,
				}),
			),
		),
	);
};

const deviceBeepField = (shared: Shared): ChildDom => {
	const onchange = (value: boolean): void => {
		shared.device.TargetConfig.Beep = value;
	};

	return div(
		{ class: "field" },
		label(
			{ class: "label" },
			() => t.smartplug.settingsPage.deviceBeepLabel,
		),
		div(
			{ class: "control" },
			onOffSelect({
				onchange,
			}),
		),
	);
};

const fotaField = (shared: Shared, save: () => void): ChildDom => {
	const onClickUpdate = (): void => {
		shared.device.Fota.Approved = true;
		save();
	};

	return div(
		{ class: "field" },
		label({ class: "label" }, "Firmware"),
		div(
			{ class: "control" },
			button(
				{
					class: "button is-primary",
					disabled: !shared.device.Fota.Filename,
					onclick: onClickUpdate,
				},
				() =>
					!shared.device.Fota.Filename ? t.update : t.latestVersion,
			),
		),
	);
};

const openCreateTimerRuleModal = (shared: Shared): void => {
	const local = reactive({
		action: "turn_on",
		days: [true, true, true, true, true, true, true],
		time: "00:00",
	});

	const createRule = (): void => {
		const [hour, minute] = local.time.split(":");
		const dow = local.days
			.map((day, i) => (day ? i : -1))
			.filter((i) => i > -1)
			.join(",");
		const newRule = {
			Action: local.action,
			Schedule: `${+minute} ${+hour} ${dow}`,
			Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		};
		shared.device.Timer.Rules = [...shared.device.Timer.Rules, newRule];
		modal.close();
	};

	const onChangeAction = (e: Event): void => {
		const target = e.target as HTMLSelectElement;
		local.action = target.value;
	};

	const onChangeDay = (e: Event, i: number): void => {
		const target = e.target as HTMLInputElement;
		const tmp = local.days;
		tmp[(i + 1) % 7] = target.checked;
		local.days = tmp;
	};

	const onInputTime = (e: Event): void => {
		const target = e.target as HTMLInputElement;
		local.time = target.value;
	};

	modal.open({
		header: t.smartplug.settingsPage.timer.newRuleLabel,
		body: [
			div(
				{ class: "field is-grouped" },
				div(
					{ class: "field" },
					label(
						{ class: "label" },
						() => t.smartplug.settingsPage.automation.actionLabel,
					),
					div(
						{ class: "control" },
						div(
							{ class: "select" },
							select(
								{
									onchange: onChangeAction,
									value: () => local.action,
								},
								option(
									{
										value: "turn_on",
									},
									() =>
										t.smartplug.settingsPage.automation
											.turnOn,
								),
								option(
									{
										value: "turn_off",
									},
									() =>
										t.smartplug.settingsPage.automation
											.turnOff,
								),
							),
						),
					),
				),
				div(
					{ class: "field" },
					label({ class: "label" }, () => t.time),
					div(
						{ class: "control" },
						input({
							class: "input",
							oninput: onInputTime,
							type: "time",
							value: () => local.time,
						}),
					),
				),
			),
			div(
				{ class: "field" },
				label({ class: "label" }, () => t.days),
				div({ class: "control" }, () =>
					div(
						{ class: "checkboxes" },
						getWeekdays().map((_day, i, weekdays) =>
							label(
								{ class: "checkbox" },
								input({
									checked: local.days[i],
									onchange: (e: Event) => onChangeDay(e, i),
									type: "checkbox",
								}),
								` ${weekdays[(i + 1) % 7].substring(0, 2)}`,
							),
						),
					),
				),
			),
		],
		footer: [
			button(
				{ class: "button is-primary", onclick: createRule },
				() => t.create,
			),
			button({ class: "button", onclick: modal.close }, () => t.cancel),
		],
	});
};

const powerFailureAlarmField = (shared: Shared): ChildDom => {
	const onchange = (value: boolean): void => {
		shared.device.PowerFailureNotification.Enabled = value;
	};

	return div(
		{ class: "field" },
		label(
			{ class: "label" },
			() => t.smartplug.settingsPage.powerFailureNotificationsLabel,
		),
		div({ class: "control" }, onOffSelect({ onchange })),
	);
};

const powerLoadAlarmField = (shared: Shared): ChildDom => {
	const onChangeEnabled = (value: boolean): void => {
		shared.device.TargetConfig.LoadAlarm.Enabled = value;
	};

	const onInputLower = (e: Event): void => {
		const target = e.target as HTMLInputElement;
		shared.device.TargetConfig.LoadAlarm.Lower = +target.value;
	};

	const onInputUpper = (e: Event): void => {
		const target = e.target as HTMLInputElement;
		shared.device.TargetConfig.LoadAlarm.Upper = +target.value;
	};

	return div(
		{ class: "field" },
		label(
			{ class: "label" },
			() => t.smartplug.settingsPage.powerLoadNotificationsLabel,
		),
		div(
			{ class: "field is-grouped" },
			div(
				{ class: "control is-expanded" },
				onOffSelect({
					onchange: onChangeEnabled,
				}),
			),
			div(
				{
					class: () =>
						shared.device.TargetConfig.LoadAlarm.Enabled
							? "field has-addons"
							: "is-hidden",
				},
				div(
					{ class: "control is-expanded" },
					input({
						class: "input",
						oninput: onInputLower,
						value: () => shared.device.TargetConfig.LoadAlarm.Lower,
					}),
				),
				div(
					{ class: "control" },
					span({ class: "button is-static" }, "–"),
				),
				div(
					{ class: "control is-expanded" },
					input({
						class: "input",
						oninput: onInputUpper,
						value: () => shared.device.TargetConfig.LoadAlarm.Upper,
					}),
				),
				div(
					{ class: "control" },
					span({ class: "button is-static" }, "W"),
				),
			),
		),
	);
};

const timerField = (shared: Shared): ChildDom => {
	const onChangeEnabled = (value: boolean): void => {
		shared.device.Timer.Enabled = value;
	};

	return div(
		{ class: "field" },
		label({ class: "label" }, () => t.smartplug.settingsPage.timer.label),
		div(
			{ class: "field is-grouped" },
			div(
				{ class: "control" },
				onOffSelect({
					onchange: onChangeEnabled,
				}),
			),
			div(
				{
					class: () =>
						shared.device.Timer.Enabled ? "control" : "is-hidden",
				},
				button(
					{
						class: "button is-primary",
						onclick: () => openCreateTimerRuleModal(shared),
					},
					() => t.create,
				),
			),
		),
		table(
			{
				class: () =>
					shared.device.Timer.Enabled ? "table" : "is-hidden",
			},
			() =>
				tbody(
					shared.device.Timer.Rules.map((rule, i) =>
						timerRuleTableRow(shared, rule, i),
					),
				),
		),
	);
};

const timerRuleTableRow = (
	shared: Shared,
	rule: TimerRule,
	i: number,
): ChildDom => {
	const [minutes, hours, dow] = rule.Schedule.split(" ");
	const removeRule = (): void => {
		shared.device.Timer.Rules = shared.device.Timer.Rules.slice(
			0,
			i,
		).concat(shared.device.Timer.Rules.slice(i + 1));
	};
	return tr(
		td(`${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`),
		td(
			dow
				.split(",")
				.map((i) => (i === "0" ? 7 : +i))
				.sort()
				.map((i) => getWeekdays()[i % 7].substring(0, 2))
				.join(" "),
		),
		td(() =>
			rule.Action === "turn_on"
				? t.smartplug.settingsPage.automation.turnOn
				: t.smartplug.settingsPage.automation.turnOff,
		),
		td(
			materialSymbol({
				id: "close",
				onclick: removeRule,
			}),
		),
	);
};
